import type {CharacterState} from './character-state';
import {BASE_ITEM_CHOICE, EquippedItem, ItemRecord, toEquipmentItem} from './equipment';
import type {FeatureData} from './catalog';
import type {FeatureOwner} from './feature-tree';
import type {PropsSource} from './props-sources';
import type {FeatureProps} from './types';

export const ITEM_OWNER_PREFIX = 'item:';

export const unique = <T>(values: T[]) => [...new Set(values)];

const itemOwnerKey = (entryKey: string) => `${ITEM_OWNER_PREFIX}${entryKey}`;

export function buildEquipment(state: CharacterState, records: Map<string, ItemRecord>): EquippedItem[] {
	return state.equipment.flatMap((entry) => {
		const record = records.get(entry.itemId);
		if (!record) return [];
		const baseId = entry.choices?.[BASE_ITEM_CHOICE]?.[0];
		const item = toEquipmentItem(record, baseId ? records.get(baseId) : undefined);
		const isAttuned = item.requiresAttunement && state.attunement.includes(entry.key);
		const isWorn = !!entry.equipped || isAttuned;
		const parts = activeParts(item.props, isWorn, isAttuned);
		const weaponBonus = {
			attackBonus: parts.reduce((total, part) => total + (part.weapon?.attackBonus || 0), 0),
			damageBonus: parts.reduce((total, part) => total + (part.weapon?.damageBonus || 0), 0),
		};
		return [{entry, item, isAttuned, isWorn, weaponBonus}];
	});
}

const activeParts = (props: EquippedItem['item']['props'], isWorn: boolean, isAttuned: boolean): FeatureProps[] =>
	[isWorn && props.equipped, isAttuned && props.attuned].filter((part): part is FeatureProps => !!part);

/** ресурсы и заклинания разных предметов не должны делить заряды: id ресурсов получают ключ записи */
function scopeResources(props: FeatureProps, prefix: string): FeatureProps {
	const scoped = (id: string | undefined) => (id ? `${prefix}:${id}` : id);
	const scopeAction = (action: FeatureProps['action']) => action && {...action, resource: scoped(action.resource)};
	return {
		...props,
		resources: props.resources?.map((resource) => ({...resource, id: scoped(resource.id)!})),
		spells: props.spells?.map((spell) => ({...spell, prepared: true, resource: scoped(spell.resource)})),
		action: scopeAction(props.action),
		bonusAction: scopeAction(props.bonusAction),
		reaction: scopeAction(props.reaction),
		magicAction: scopeAction(props.magicAction),
		free: scopeAction(props.free),
	};
}

export function itemPropsSources(equipment: EquippedItem[], level: number): PropsSource[] {
	return equipment.flatMap(({entry, item, isWorn, isAttuned}) => {
		const owner: FeatureOwner = {key: itemOwnerKey(entry.key), level};
		const feature: FeatureData = {
			id: `${ITEM_OWNER_PREFIX}${item.id}`,
			title: item.title,
			description: '',
			levels: [],
			order: 0,
			choose: item.choose,
		};
		return activeParts(item.props, isWorn, isAttuned).map(
			(props, index): PropsSource => ({
				key: `${owner.key}#${index}`,
				featureKey: owner.key,
				props: scopeResources(props, entry.key),
				feature,
				title: item.title,
				owner,
				scopes: [owner.key],
			}),
		);
	});
}

export const itemChoiceValues = (equipment: EquippedItem[]) =>
	new Map(equipment.map(({entry}) => [itemOwnerKey(entry.key), entry.choices || {}]));
